import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import {
  ChatMessage, ChatState, SSEEvent, ToolUseEvent,
  FeedbackRequest, ChatAttachment, ContentBlock,
} from '../models/chat.models';
import { ChatApiService } from '../services/chat-api.service';
import { ChatConfigService } from '../services/chat-config.service';

let _seq = 0;
function uid(): string { return `msg_${Date.now()}_${++_seq}`; }

const INIT: ChatState = {
  sessions: [],
  activeSessionId: null,
  messages: {},
  isStreaming: false,
  isLoadingSessions: false,
  isLoadingMessages: false,
  error: null,
  controllers: [],
  activeControllerId: localStorage.getItem('chat_controller_id') || 'production',
  isOpen: false,
  isSidebarOpen: false,
  isFullscreen: false,
  sessionsMax: 50,
};

@Injectable()
export class ChatStateService {

  private _state = new BehaviorSubject<ChatState>({ ...INIT });
  readonly state$ = this._state.asObservable();

  private streamSub: Subscription | null = null;
  private controllerResolved = false;

  constructor(
    private api: ChatApiService,
    private config: ChatConfigService,
    private zone: NgZone,
  ) {
    this.resolveController();
  }

  get snapshot(): ChatState { return this._state.getValue(); }

  private patch(p: Partial<ChatState>): void {
    this._state.next({ ...this.snapshot, ...p });
  }

  // ── controller ────────────────────────────────────────────────────────────

  private resolveController(): void {
    if (this.controllerResolved) return;
    this.config.resolveControllerId().then(id => {
      this.controllerResolved = true;
      this.patch({ activeControllerId: id });
    });
  }

  // ── ui ────────────────────────────────────────────────────────────────────

  open(): void   { this.patch({ isOpen: true });  this.loadSessions(); }
  close(): void  { this.patch({ isOpen: false, isFullscreen: false }); }
  toggle(): void {
    const next = !this.snapshot.isOpen;
    this.patch({ isOpen: next, isFullscreen: next ? this.snapshot.isFullscreen : false });
    if (next) this.loadSessions();
  }
  toggleSidebar(): void   { this.patch({ isSidebarOpen: !this.snapshot.isSidebarOpen }); }
  toggleFullscreen(): void { this.patch({ isFullscreen: !this.snapshot.isFullscreen }); }
  exitFullscreen(): void   { this.patch({ isFullscreen: false }); }
  clearError(): void       { this.patch({ error: null }); }

  // ── sessions ──────────────────────────────────────────────────────────────

  loadSessions(): void {
    if (this.snapshot.isLoadingSessions) return;
    this.patch({ isLoadingSessions: true });
    this.api.getSessions().subscribe({
      next:  r  => this.patch({
        sessions:    r.sessions || [],
        sessionsMax: r.max ?? this.snapshot.sessionsMax,
        isLoadingSessions: false,
      }),
      error: e  => this.patch({ error: e.message, isLoadingSessions: false }),
    });
  }

  selectSession(id: string): void {
    this.patch({ activeSessionId: id });
    // always reload to get latest messages
    this._loadMsgs(id);
  }

  private _loadMsgs(sessionId: string): void {
    this.patch({ isLoadingMessages: true });
    this.api.getSession(sessionId).subscribe({
      next: detail => {
        // The session API returns messages in TWO possible shapes:
        //
        //  Shape A (new format):  { role, content: [{type:'text', text}] }
        //  Shape B (saved format): { role, text: "...", tools: [...], attachments: [] }
        //
        // Normalise everything to Shape A so the rest of the UI is consistent.
        const raw: any[] = (detail as any).messages || [];
        const msgs: ChatMessage[] = raw.map((m: any) => {
          const id = uid();

          // ── normalise content ──────────────────────────────────────
          let content: ContentBlock[] = [];
          if (Array.isArray(m.content) && m.content.length) {
            // Already Shape A
            content = m.content;
          } else if (typeof m.text === 'string' && m.text) {
            // Shape B — wrap plain text into ContentBlock
            content = [{ type: 'text', text: m.text }];
          }

          // ── normalise tool calls (Shape B has tools:[]) ────────────
          let toolUses: ToolUseEvent[] = [];
          if (Array.isArray(m.tools) && m.tools.length) {
            toolUses = m.tools.map((t: any) => ({
              id:       t.id   || uid(),
              name:     t.name || 'tool',
              input:    t.input || {},
              result:   t.result?.text || t.result || '',
              status:   t.result?.isError ? 'error' : 'done',
              expanded: false,
            } as ToolUseEvent));
          }

          return {
            id,
            role:      m.role,
            content,
            timestamp: new Date(),
            sessionId,
            toolUses:  toolUses.length ? toolUses : undefined,
            // If there's an error flag on the message (tool loop cap etc.)
            error:     m.error || undefined,
          } as ChatMessage;
        });

        this.patch({
          messages: { ...this.snapshot.messages, [sessionId]: msgs },
          isLoadingMessages: false,
        });
      },
      error: e => this.patch({ error: e.message, isLoadingMessages: false }),
    });
  }

  createNewSession(): void {
    // Don't pre-create on the server — just show the welcome screen.
    // The session is created automatically (using the first message as title)
    // when sendMessage() is called with activeSessionId === null.
    this.patch({ activeSessionId: null });
  }

  deleteSession(id: string): void {
    this.api.deleteSession(id).subscribe({
      next: () => {
        const sessions = this.snapshot.sessions.filter(s => s.id !== id);
        const messages = { ...this.snapshot.messages };
        delete messages[id];
        this.patch({
          sessions,
          messages,
          activeSessionId: this.snapshot.activeSessionId === id
            ? (sessions[0]?.id || null)
            : this.snapshot.activeSessionId,
        });
      },
      error: e => this.patch({ error: e.message }),
    });
  }

  renameSession(id: string, title: string): void {
    this.api.updateSession(id, title).subscribe({
      next:  u => this.patch({ sessions: this.snapshot.sessions.map(s => s.id === id ? { ...s, title: u.title } : s) }),
      error: e => this.patch({ error: e.message }),
    });
  }

  /**
   * Compact session: summarises transcript → frees context tokens.
   * Reloads session list afterward to refresh token counts.
   */
  compactSession(id: string): void {
    this.api.compactSession(id).subscribe({
      next:  () => this.loadSessions(),
      error: e  => this.patch({ error: e.message }),
    });
  }

  /**
   * Release tokens: zeros the token meter for the session.
   * Updates the local session token counts immediately for snappy UI.
   */
  resetSessionUsage(id: string): void {
    this.api.resetSessionUsage(id).subscribe({
      next: (res) => {
        const zeroTokens = res?.tokens ?? {
          input: 0, output: 0, lastInput: 0, lastOutput: 0, total: 0, turns: 0,
        };
        this.patch({
          sessions: this.snapshot.sessions.map(s =>
            s.id === id ? { ...s, tokens: zeroTokens } : s
          ),
        });
      },
      error: e => this.patch({ error: e.message }),
    });
  }

  // ── chat ──────────────────────────────────────────────────────────────────

  sendMessage(text: string, attachments?: ChatAttachment[]): void {
    if (!text.trim() || this.snapshot.isStreaming) return;

    const userMsg: ChatMessage = {
      id: uid(), role: 'user',
      content: [{ type: 'text', text }],
      timestamp: new Date(),
      sessionId: this.snapshot.activeSessionId || undefined,
    };

    if (!this.snapshot.activeSessionId) {
      this.api.createSession(text.slice(0, 60)).subscribe({
        next: s => {
          this.patch({
            sessions: [s, ...this.snapshot.sessions],
            activeSessionId: s.id,
            messages: { ...this.snapshot.messages, [s.id]: [userMsg] },
          });
          this._stream(s.id, text, userMsg, attachments);
        },
        error: e => this.patch({ error: e.message }),
      });
      return;
    }

    const sid = this.snapshot.activeSessionId;
    this._addMsg(sid, userMsg);
    this._stream(sid, text, userMsg, attachments);
  }

  private _stream(
    sid: string, text: string,
    userMsg: ChatMessage, attachments?: ChatAttachment[]
  ): void {
    const history = (this.snapshot.messages[sid] || [])
      .filter(m => !m.isStreaming && m.id !== userMsg.id)
      .map(m => ({ role: m.role, content: m.content }));

    const aId = uid();
    this._addMsg(sid, {
      id: aId, role: 'assistant', content: [],
      timestamp: new Date(), sessionId: sid,
      isStreaming: true, streamText: '', toolUses: [],
    });

    this.patch({ isStreaming: true, error: null });

    this.streamSub?.unsubscribe();
    this.streamSub = this.api.streamChat({ message: text, history, session_id: sid, attachments })
      .subscribe({
        next:     evt => this._onSSE(sid, aId, evt),
        error:    err => {
          this._patchMsg(sid, aId, { isStreaming: false, error: err.message || 'Stream error' });
          this.patch({ isStreaming: false });
        },
        complete: ()  => {
          this._patchMsg(sid, aId, m => ({
            isStreaming: false,
            content: m.streamText ? [{ type: 'text' as const, text: m.streamText }] : m.content,
          }));
          // Optimistically bump local messageCount (+2: user + assistant)
          const sessions = this.snapshot.sessions.map(s =>
            s.id === sid ? { ...s, messageCount: (s.messageCount || 0) + 2 } : s
          );
          this.patch({ isStreaming: false, sessions });
          // Persist the full conversation to the session so it survives navigation
          this._persistMessages(sid);
        },
      });
  }

  private _onSSE(sid: string, aId: string, evt: SSEEvent): void {
    switch (evt.type) {
      case 'delta':
        if (evt.text) {
          this._patchMsg(sid, aId, m => ({
            streamText: (m.streamText || '') + evt.text,
          }));
        }
        break;

      case 'done':
        // authoritative final text — use if deltas were missed
        if ((evt as any).final_text) {
          this._patchMsg(sid, aId, { streamText: (evt as any).final_text });
        }
        break;

      case 'tool_use':
        this._patchMsg(sid, aId, m => ({
          toolUses: [...(m.toolUses || []), {
            id:       evt.tool_id   || uid(),
            name:     evt.tool_name || 'tool',
            input:    evt.tool_input || {},
            status:   'running' as const,
            expanded: false,
          } as ToolUseEvent],
        }));
        break;

      case 'tool_result':
        if (!(evt as any).hidden) {
          this._patchMsg(sid, aId, m => ({
            toolUses: (m.toolUses || []).map(t =>
              t.id === evt.tool_id
                ? { ...t, result: evt.tool_result, status: 'done' as const }
                : t
            ),
          }));
        }
        break;

      case 'error':
        this._patchMsg(sid, aId, { isStreaming: false, error: evt.error || 'Unknown error' });
        this.patch({ isStreaming: false });
        break;

      case 'auth_expired':
      case 'anthropic_auth_expired':
        this.patch({ error: 'Session expired. Please log in again.', isStreaming: false });
        this._patchMsg(sid, aId, { isStreaming: false });
        break;

      case 'anthropic_overloaded':
        this.patch({ error: 'AI service busy. Please try again shortly.', isStreaming: false });
        this._patchMsg(sid, aId, { isStreaming: false });
        break;

      default:
        break;
    }
  }

  /**
   * PUT all messages for `sid` to the backend so the session is durable.
   * Converts from internal ChatMessage → the API's { role, text, tools:[] } shape.
   */
  private _persistMessages(sid: string): void {
    const msgs = this.snapshot.messages[sid] || [];
    const session = this.snapshot.sessions.find(s => s.id === sid);

    const payload = msgs
      .filter(m => !m.isStreaming)
      .map(m => ({
        role: m.role,
        text: m.streamText || m.content?.find(c => c.type === 'text')?.text || '',
        tools: [] as any[],
      }));

    this.api.saveSessionMessages(sid, payload, session?.title).subscribe({
      next:  () => this.loadSessions(),
      error: e  => { this.patch({ error: e.message }); this.loadSessions(); },
    });
  }

  stopStream(): void {
    this.streamSub?.unsubscribe();
    const sid = this.snapshot.activeSessionId;
    if (sid) {
      const last = (this.snapshot.messages[sid] || []).slice(-1)[0];
      if (last?.isStreaming) this._patchMsg(sid, last.id, { isStreaming: false });
    }
    this.patch({ isStreaming: false });
  }

  submitFeedback(msgId: string, verdict: 'positive' | 'negative', correction?: string): void {
    const sid = this.snapshot.activeSessionId;
    if (!sid) return;
    const msgs = this.snapshot.messages[sid] || [];
    const ai = msgs.find(m => m.id === msgId);
    if (!ai) return;
    const user = msgs[msgs.indexOf(ai) - 1];

    const req: FeedbackRequest = {
      user_text:      user?.content?.find(c => c.type === 'text')?.text || '',
      assistant_text: ai.streamText || ai.content?.find(c => c.type === 'text')?.text || '',
      verdict,
      correction: correction || null,
      session_id: sid,
    };

    this.api.submitFeedback(req).subscribe({
      next:  r => this._patchMsg(sid, msgId, { feedback: verdict, feedbackId: r.id }),
      error: e => this.patch({ error: e.message }),
    });
  }

  toggleToolExpand(sid: string, msgId: string, toolId: string): void {
    this._patchMsg(sid, msgId, m => ({
      toolUses: (m.toolUses || []).map(t =>
        t.id === toolId ? { ...t, expanded: !t.expanded } : t
      ),
    }));
  }

  // ── helpers ───────────────────────────────────────────────────────────────

  private _addMsg(sid: string, msg: ChatMessage): void {
    const existing = [...(this.snapshot.messages[sid] || [])];
    this.patch({ messages: { ...this.snapshot.messages, [sid]: [...existing, msg] } });
  }

  private _patchMsg(
    sid: string, msgId: string,
    patchOrFn: Partial<ChatMessage> | ((m: ChatMessage) => Partial<ChatMessage>)
  ): void {
    const msgs = this.snapshot.messages[sid] || [];
    const updated = msgs.map(m => {
      if (m.id !== msgId) return m;
      const p = typeof patchOrFn === 'function' ? patchOrFn(m) : patchOrFn;
      return { ...m, ...p };
    });
    this.patch({ messages: { ...this.snapshot.messages, [sid]: [...updated] } });
  }

  getActiveMessages(): ChatMessage[] {
    const sid = this.snapshot.activeSessionId;
    return sid ? [...(this.snapshot.messages[sid] || [])] : [];
  }
}